import PropTypes from 'prop-types';
import qs from 'qs';
import { Component, Fragment } from 'react';
import { Link } from 'react-router';
import _ from 'underscore';

import { renderPackage, renderRevision } from '../../../shared/helpers/renderLocator';
import Locator from '../../../shared/Locator';
import { get } from '../../redux/api';
import utils from '../../utils';
import Loader from '../generic/Loader';
import Tooltip from '../generic/Tooltip';
import type { ILoc } from '../../../typings/common';
import type { Project, Revision } from '../../types';

import type { RevisionRouteParams } from './projectDashboardTypes';
import type { Paths, RevisionPaths } from './types';

type LicenseBundle = {
  licenses: string[];
  revisions: Revision[];
  description: string;
};

// TODO: pass in revs as props, bulk load them in as "deps"

type TodoItemSourcePanelProps = {
  revisionId?: string;
  role?: string;
  termId: string;
  onSelectLicense(licenseId: string): void;
  params: RevisionRouteParams;
};

// TODO: Refactor to make react-like
class TodoItemSourcePanel extends Component<
  TodoItemSourcePanelProps,
  {
    sections: LicenseBundle[];
    loading: boolean;
  }
> {
  constructor(props) {
    super(props);
    this.state = {
      sections: [],
      loading: true,
    };
  }

  componentDidMount() {
    // load from role and term
    // maybe all this caching should be store based
    // id: revision + termId
    // TODO: move to Store
    this.setState({ loading: true });
    get(
      `/api/revisions/${utils.nuc(this.props.revisionId)}/todos`,
      false,
    ).then(res => {
      const license_bundles = res.data[this.props.role][this.props.termId];
      const desc_bundles: Record<string, LicenseBundle> = {};
      _.each(license_bundles, (bundle: any) => {
        if (!bundle) {
          return;
        }
        const indexed_bundle = desc_bundles[bundle.description];
        if (!indexed_bundle) {
          desc_bundles[bundle.description] = {
            licenses: [bundle.license],
            revisions: bundle.revisions,
            description: bundle.description,
          };
        } else {
          indexed_bundle.revisions = _.union(indexed_bundle.revisions, bundle.revisions);
          indexed_bundle.licenses.push(bundle.license);
        }
      });
      return this.setState({
        loading: false,
        sections: _.values(desc_bundles),
      });
    }).catch((_err) => null);
  }

  render() {
    if (this.state.loading) {
      return (
        <div className="text-center" style={{ fontSize: '40px', lineHeight: '90px', color: '#ccc' }}>
          <Loader size="90" />
        </div>
      );
    }

    return <>
      {this.state.sections.map((section, i) => {
        if (!section.description) {
          return null;
        }

        return (
          <Fragment key={i}>
            <Tooltip
              placement="left"
              tooltip={
                <Tooltip id="tldrLegalTooltip">
                  <b>Disclaimer:</b> These are summaries sourced from TLDRLegal.
                </Tooltip>
              }
            >
              <div className="attr_text">
                <p>&ldquo;{utils.boldWords(section.description, this.props.termId)}&rdquo;</p>
                <div className="attr_source">
                  {_.uniq(section.licenses).map((licenseId, j) => (
                    <span key={licenseId}>
                      <a onClick={() => this.props.onSelectLicense(licenseId)} className="btn-view-summary clickable">
                        {licenseId}
                      </a>
                      {section.licenses.length - 1 > j ? ' & ' : ''}
                    </span>
                  ))}
                </div>
              </div>
            </Tooltip>
            <TodoSourceRefPanel
              params={this.props.params}
              revisionId={this.props.revisionId}
              refs={section.revisions}
              initExpanded={section.revisions.length < 7}
            />
          </Fragment>
        );
      })}
    </>;
  }
}

class TodoSourceRefPanel extends Component<
  {
    params: RevisionRouteParams;
    initExpanded?: boolean;
    refs?: string[];
    revisionId?: string;
  },
  {
    paths: RevisionPaths;
    isExpanded: boolean;
    loading: boolean;
  }
> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      isExpanded: !!this.props.initExpanded,
      paths: [],
      loading: true,
    };
  }

  componentDidMount() {
    get(
      `/api/revisions/${utils.nuc(this.props.revisionId)}/paths`,
      false,
      {
        locator: _.map(this.props.refs, utils.nuc),
        maxResults: 100 * this.props.refs.length,
      },
    ).then(resp => {
      const paths = resp.data;
      return this.setState({ paths, loading: false });
    }).catch(() => {
      return this.setState({ loading: false });
    });
  }

  //  Uses whatever the revisionId is to get the package string.
  getParentProjectPackageLocator = () => new Locator(this.props.revisionId as string);

  getLinkToProjectDepenency = (locator?: string) => {
    const { params } = this.props;
    const parentLocator = this.getParentProjectPackageLocator();

    const baseURL = [
      '/projects',
      utils.nuc(parentLocator.toPackageString()),
      'refs',
      utils.nuc(params.reference_type),
      utils.nuc(params.reference),
      parentLocator.revision.value,
      'browse',
      'dependencies',
    ].join('/');

    //  There are cases below where this could be falsey,
    //  and we don't want to send the user to an invalid URL/searching for a package called "undefined".
    if (!locator) {
      return baseURL;
    }

    const { package: packageTitle } = Locator.parse(locator);
    const queryString = qs.stringify({ title: packageTitle }, { addQueryPrefix: true });
    return `${baseURL}${queryString}`;
  };

  onExpand = () => this.setState({ isExpanded: true });

  onRefClick = (source) => this.context.router.push(
    this.getLinkToProjectDepenency(source.locator),
  );

  render() {
    const { isExpanded, loading } = this.state;

    if (!isExpanded) {
      return (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div className="collapse-bar" onClick={this.onExpand}>
          {this.props.refs.length} Projects...
        </div>
      );
    }

    if (loading) {
      return <Loader />;
    }

    const renderedSourceRefs = (this.props.refs?.filter(ref => ref === this.props.revisionId) ?? [])
      .map((ref, index) => (
        <li key={ref || `this-rev-${index}`} className="text-center">
          <span className="badge">This Project</span>
        </li>
      ));

    for (let z = 0; z < this.state.paths.length; z++) {
      let source: {
        locator?: string;
        title?: string;
        loc?: ILoc;
        paths?: Paths;
        project?: Project;
      } = (path_collection => {
        if (!path_collection || path_collection.length === 0) {
          return {};
        }
        const path = path_collection[0].slice(-1)[0];
        const { locator, title } = path;
        return {
          locator, // get target path
          title,
          loc: Locator.parse(locator),
          paths: path_collection,
          project: {
            description: 'TBA',
          },
        };
      })(this.state.paths[z]);
      let depLabel;
      if (source.paths?.length > 1) {
        depLabel = (
          <div className="dep-label">
            Depended on in {source.paths[0].length >= 99 ? '99+' : source.paths[0].length} ways
          </div>
        );
      } else if (source.paths?.[0].length > 1 && source.paths.length === 1) {
        depLabel = ( // todo: show loader if not populated
          <div className="dep-label" onClick={utils.stopEventPropagation}>
            {utils.numSuffix(source.paths[0].length)}-level dep of{' '}
            <Link to={this.getLinkToProjectDepenency(source.paths?.[0]?.[0]?.locator)}>
              {utils.trimString(source.title || renderPackage(source.paths[0][0]?.locator), 18)}
            </Link>
          </div>
        );
      }
      if (!source.project) {
        continue;
      }
      renderedSourceRefs.push(
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
        <li key={`expanded-source-${z}`} className="clickable" onClick={() => this.onRefClick(source)}>
          <div className="source-content">
            <Link to={this.getLinkToProjectDepenency(source.locator)}>
              {utils.trimString(source.title || source.project.title || renderPackage(source.loc), 23)}{' '}
              <b>({utils.trimString(renderRevision(source.loc), 8)})</b>
            </Link>{' '}
            {depLabel}
          </div>
        </li>,
      );
    }
    return (
      <div>
        <ul className="sources-list list list-clean">{renderedSourceRefs}</ul>
      </div>
    );
  }
}

export default TodoItemSourcePanel;
